import { MS_PER_GAME_HOUR, formatDateTime } from '@pk/shared';
import type { WorldView } from '@pk/shared';

type Clock = Pick<WorldView, 'day' | 'nextDayAtMs' | 'msPerDay'>;

/** Real milliseconds until the start of game day `day` (0 if already reached). */
export function msUntilDay(w: Clock, day: number, nowMs = Date.now()): number {
  const d = Math.floor(day) - w.day;
  if (d <= 0) return 0;
  return Math.max(0, w.nextDayAtMs - nowMs) + (d - 1) * w.msPerDay;
}

/** Real ms when game day 0 started (derived from the clock the server sent). */
export function epochMsOf(w: Clock): number {
  return w.nextDayAtMs - (w.day + 1) * w.msPerDay;
}

/** Current absolute in-game hour (day * 24 + hour), from the epoch and the real clock. */
export function currentAbsHour(epochMs: number, nowMs = Date.now()): number {
  return Math.floor((nowMs - epochMs) / MS_PER_GAME_HOUR);
}

/** Real ms until the given absolute in-game hour starts (0 if already reached). */
export function msUntilAbsHour(absHour: number, epochMs: number, nowMs = Date.now()): number {
  return Math.max(0, (absHour - currentAbsHour(epochMs, nowMs)) * MS_PER_GAME_HOUR);
}

/** "14 September 1010 · 06:00 (~2 h)" — exact in-game hour plus its real-time ETA. */
export function formatHourEta(absHour: number, epochMs: number, t: T, nowMs = Date.now()): string {
  return `${formatDateTime(absHour)} (${formatEta(msUntilAbsHour(absHour, epochMs, nowMs), t)})`;
}

/** Real ms for a span of game days. */
export function msForDays(w: Pick<WorldView, 'msPerDay'>, days: number): number {
  return days * w.msPerDay;
}

type T = (key: string, opts?: Record<string, unknown>) => string;

/** "~12 min", "~2 h 10 min", "<1 min". */
export function formatEta(ms: number, t: T): string {
  const min = Math.round(ms / 60_000);
  if (min < 1) return t('time.lessThanMin');
  if (min < 60) return t('time.minutes', { count: min });
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h >= 48) return t('time.days', { count: Math.round(h / 24) });
  return m === 0 ? t('time.hours', { count: h }) : t('time.hoursMinutes', { h, m });
}
